/**
 * useSkillsMeta — Fetches skill metadata (tier, verified, description_pt, requires_api_key)
 * from the skills-pack manifest + tier2 recommended endpoint.
 * Returns a Map<name, metadata> for easy lookup by other components.
 */
import { useState, useEffect } from 'preact/hooks';
import { api } from '../../api/client';

export interface SkillMetaInfo {
  tier?: string;
  verified?: number;
  badge?: string;
  description_pt?: string;
  requires_api_key?: number;
  required_keys?: string;
  emoji?: string;
  category?: string;
}

let cachedMeta: Map<string, SkillMetaInfo> | null = null;

export function useSkillsMeta(): { meta: Map<string, SkillMetaInfo>; loading: boolean } {
  const [meta, setMeta] = useState<Map<string, SkillMetaInfo>>(cachedMeta || new Map());
  const [loading, setLoading] = useState(!cachedMeta);

  useEffect(() => {
    if (cachedMeta) return;
    const map = new Map<string, SkillMetaInfo>();

    Promise.all([
      api.get<any>('/cloud/skills-pack/manifest').catch(() => null),
      api.get<any>('/cloud/skills-pack/tier2/recommended').catch(() => null),
    ]).then(([manifest, tier2]) => {
      const mSkills = (manifest as any)?.skills || (manifest as any)?.data?.skills || [];
      for (const s of mSkills) {
        map.set(s.name, {
          tier: s.tier || 'tier1',
          verified: s.verified ?? 1,
          badge: s.badge || 'verified',
          description_pt: s.description_pt,
          requires_api_key: s.requires_api_key,
          required_keys: s.required_keys,
          emoji: s.emoji,
          category: s.category,
        });
      }

      const t2Skills = (tier2 as any)?.skills || (tier2 as any)?.data?.skills || (tier2 as any)?.data || [];
      if (Array.isArray(t2Skills)) {
        for (const s of t2Skills) {
          if (!map.has(s.name)) {
            map.set(s.name, {
              tier: 'tier2',
              verified: 0,
              badge: 'recommended',
              description_pt: s.description_pt,
              requires_api_key: s.requires_api_key,
              required_keys: s.required_keys,
              emoji: s.emoji,
              category: s.category,
            });
          }
        }
      }

      cachedMeta = map;
      setMeta(map);
    }).finally(() => setLoading(false));
  }, []);

  return { meta, loading };
}

/** Enrich a skill name with metadata from the map */
export function enrichSkill(name: string, meta: Map<string, SkillMetaInfo>): SkillMetaInfo {
  return meta.get(name) || { tier: 'optional', badge: 'community' };
}
